import { ClearPressResponder } from '@react-aria/interactions';
import clsx from 'clsx';
import { addYears, endOfDay, parseISO, startOfDay } from 'date-fns';
import { useMemo, useState } from 'react';
import { useLabel } from 'react-aria';
import { Button, DialogTrigger, Label, Text } from 'react-aria-components';
import CalendarRangePopover, { PresetDate } from '~/components/inputs/DatePicker/CalendarRangePopover';
import fieldStyles from '~/components/inputs/Field.module.scss';
import CalendarIcon from '~/icons/icon-calendar.svg?react';
import IconClose from '~/icons/icon-close.svg?react';
import { DateRangeItem } from '~/types/date';
import { dateToCalendarDate, useFormatRange } from '~/utils/date';
import styles from './DateRangeFilter.module.scss';
import IconButton from '../buttons/NewIconButton';
import Icon from '../data-display/Icon';
import { HStack } from '../layout/Stack';

export type DateRangeFilterValue = [string | null, string | null];

type NameType<T> = T extends string ? string : T extends {} ? keyof T : string;

interface DateRangeFilterChangeMeta {
  source: 'calendar' | 'clear' | 'preset';
}
interface DateRangeFilterProps<Filters extends string | {}> {
  className?: string;
  clearable?: boolean;
  disabled?: boolean;
  label: string;
  hideLabel?: boolean;
  maxRangeDays?: number;
  placeholder?: string;
  name: NameType<Filters>;
  onChange: (name: NameType<Filters>, value: DateRangeFilterValue, meta?: DateRangeFilterChangeMeta) => void;
  ranges?: DateRangeItem[];
  value: DateRangeFilterValue;
}

const MIN_DATE = addYears(new Date(), -100);
const MAX_DATE = addYears(new Date(), 100);

const DateRangeFilter = <Filters extends string | {}>({
  className,
  clearable = false,
  disabled = false,
  label,
  hideLabel,
  maxRangeDays,
  placeholder = label,
  name,
  onChange,
  ranges,
  value,
}: DateRangeFilterProps<Filters>) => {
  const [start, end] = value;
  const hasNoValue = !start && !end;
  const [isOpen, setIsOpen] = useState(false);
  const formatRange = useFormatRange();
  const { labelProps, fieldProps } = useLabel(
    !hideLabel && label
      ? {
          label,
        }
      : {
          'aria-label': label,
        },
  );

  const handleChange = (dateRange: DateRangeFilterValue, meta?: DateRangeFilterChangeMeta) => {
    onChange(name, dateRange, meta);
  };

  const presetDates = useMemo<PresetDate[] | undefined>(() => {
    if (!ranges?.length) return undefined;

    const mappedRanges = ranges.map(({ startDate, endDate, label }, index) => ({
      date: {
        end: endDate ? endOfDay(endDate) : undefined,
        start: startDate ? startOfDay(startDate) : undefined,
      },
      id: `date-range-${index}-${startDate?.toISOString()}-${endDate?.toISOString()}`,
      label: label ?? formatRange(startDate, endDate),
    }));

    return mappedRanges.length ? mappedRanges : undefined;
  }, [formatRange, ranges]);

  const calendarValue = hasNoValue
    ? null
    : {
        // RangeCalendar requires both bounds when a value is provided.
        // We map missing endpoints to very wide sentinels so open-ended filters
        // (from-only / until-only) can still be represented in the calendar.
        start: dateToCalendarDate(start ? parseISO(start) : MIN_DATE),
        end: dateToCalendarDate(end ? parseISO(end) : MAX_DATE),
      };

  const showClear = clearable && !hasNoValue && !disabled;
  return (
    <>
      <div className={clsx(fieldStyles.field, styles.container, className)} data-size="medium" data-open={isOpen}>
        {!hideLabel && <Label {...labelProps}>{label}</Label>}

        <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen}>
          <div className={styles.triggerContainer}>
            <Button {...fieldProps} isDisabled={disabled} className={styles.valueContainer}>
              <Text className={styles.value} data-placeholder={hasNoValue}>
                {hasNoValue
                  ? placeholder
                  : formatRange(start ? parseISO(start) : undefined, end ? parseISO(end) : undefined)}
              </Text>

              <HStack justify="center" align="center">
                {showClear && <Icon component={IconClose} className={clsx(styles.clearPlaceholder)} />}
                <Icon component={CalendarIcon} className={styles.icon} />
              </HStack>
            </Button>

            {showClear && (
              <ClearPressResponder>
                <IconButton
                  className={clsx(styles.icon, styles.clear)}
                  Icon={IconClose}
                  variant="ghost"
                  size="small"
                  onPressStart={() => handleChange([null, null], { source: 'clear' })}
                />
              </ClearPressResponder>
            )}
          </div>
          <CalendarRangePopover
            presetDates={presetDates}
            maxRangeDays={maxRangeDays}
            value={calendarValue}
            defaultFocusedValue={!start ? dateToCalendarDate(new Date()) : undefined}
            onSelectDate={(val, preset) => {
              const startDate = val.start ? startOfDay(val.start).toISOString() : null;
              const endDate = val.end ? endOfDay(val.end).toISOString() : null;
              handleChange([startDate, endDate], { source: preset ? 'preset' : 'calendar' });
              setIsOpen(false);
            }}
          />
        </DialogTrigger>
      </div>
    </>
  );
};

export default DateRangeFilter;
